import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Typography,
  Switch,
  Divider,
  Space,
  Button,
  message,
  notification,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Alert,
  Spin,
  Tabs,
  Select,
  Table,
  Modal,
  Popconfirm,
  Collapse,
  Tag,
  Descriptions,
} from 'antd';
import { SaveOutlined, MailOutlined, SettingOutlined, SendOutlined, PhoneOutlined, UserOutlined, TableOutlined, AppstoreOutlined, EyeOutlined, TeamOutlined, KeyOutlined, CopyOutlined, DeleteOutlined, InfoCircleOutlined, DownloadOutlined, FileTextOutlined, LaptopOutlined, DesktopOutlined, CustomerServiceOutlined, PlusOutlined, UpOutlined, DownOutlined, FileSearchOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import RoleManagement from '../components/RoleManagement';
import Equipment from '../pages/Equipment';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

interface SystemSettings {
  systemName: string;
  systemVersion: string;
  maintenanceMode: boolean;
  allowUserRegistration: boolean;
  maxFileUploadSize: string;
  sessionTimeout: string;
  enableAuditLog: boolean;
  enableEmailNotifications: boolean;
}

interface TicketAuditSettings {
  frequency: 'weekly' | 'biweekly' | 'monthly';
  poolSize: number;
  sampleSize: number;
  targetRoles: string[];
}

interface EmailSettings {
  smtpHost: string;
  smtpPort: string;
  smtpUsername: string;
  smtpPassword: string;
  smtpSecure: boolean;
  fromEmail: string;
  fromName: string;
  enableBusinessNotifications: boolean;
  enableAgentNotifications: boolean;
  enableSystemAlerts: boolean;
  enableFeedbackRequests: boolean;
}

interface RingCentralSettings {
  server: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  webhookUrl: string;
  enableCallLogging: boolean;
  enableCallRecording: boolean;
  defaultCallerId: string;
}


interface UISettings {
  defaultPageSize: number;
  enableDarkMode: boolean;
  enableNotifications: boolean;
  enableSoundAlerts: boolean;
  autoRefreshInterval: number;
  showAdvancedFilters: boolean;
  compactMode: boolean;
}

interface BusinessSettings {
  serviceTypes: string[];
  equipmentTypes: string[];
  defaultCountry: string;
  requireMID: boolean;
  enableRetailZPOSPortal: boolean;
}

interface MacroRule {
  field_key: string;
  equals: string;
}

interface TicketMacro {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  apply_to_comment_type: 'both' | 'public' | 'internal';
  auto_apply: boolean;
  apply_on_new_ticket?: boolean;
  auto_apply_category_ids: string[];
  auto_apply_ticket_types: string[];
  auto_apply_custom_field_rules: MacroRule[];
}

interface TvDashboardNewsItem {
  id: string;
  title: string;
  content: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [systemForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [templatesForm] = Form.useForm();
  const [ringCentralForm] = Form.useForm();
  const [uiForm] = Form.useForm();
  const [businessForm] = Form.useForm();
  
  const [testEmail, setTestEmail] = useState('');
  const [categoryForm] = Form.useForm();
  const [fieldForm] = Form.useForm();
  const [ticketTypeForm] = Form.useForm();
  const [macroForm] = Form.useForm();
  const [apiTokenForm] = Form.useForm();
  const [newTokenValue, setNewTokenValue] = useState<string | null>(null);
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [tvDashboardForm] = Form.useForm();
  const [showTvPassword, setShowTvPassword] = useState(false);
  const [announcementForm] = Form.useForm();
  const [tvNewsForm] = Form.useForm();
  const [tvNewsModalVisible, setTvNewsModalVisible] = useState(false);
  const [editingTvNews, setEditingTvNews] = useState<TvDashboardNewsItem | null>(null);
  const [ticketAuditForm] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState<{ [key: string]: boolean }>({});
  const [emailTemplateClickCount, setEmailTemplateClickCount] = useState<number>(0);
  const [resetAllModalVisible, setResetAllModalVisible] = useState<boolean>(false);
  const [macroSearch, setMacroSearch] = useState<string>('');
  const [macroModalVisible, setMacroModalVisible] = useState<boolean>(false);
  const [editingMacro, setEditingMacro] = useState<TicketMacro | null>(null);
  const [macroFilterType, setMacroFilterType] = useState<'all' | 'both' | 'public' | 'internal'>('all');
  const [macroFilterAutoApply, setMacroFilterAutoApply] = useState<'all' | 'auto' | 'manual'>('all');
  const [macroFilterStatus, setMacroFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedMacroRowKeys, setSelectedMacroRowKeys] = useState<React.Key[]>([]);
  const [macroBulkLoading, setMacroBulkLoading] = useState<boolean>(false);

  const handleResetEmailTemplate = async (templateName: string, label: string) => {
    try {
      await api.post('/api/settings/email-templates/reset', { templateName });
      message.success(`${label} template reset to defaults`);
      queryClient.invalidateQueries('settings');
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Failed to reset template');
    }
  };

  // Categories
  const { data: categories = [], refetch: refetchCategories } = useQuery('categories', async () => {
    const res = await api.get('/api/categories');
    return res.data?.categories || [];
  }, { staleTime: 5 * 60 * 1000 });

  const createCategory = useMutation(async (values: any) => {
    const res = await api.post('/api/categories', values);
    return res.data;
  }, {
    onSuccess: () => { message.success('Category created'); refetchCategories(); categoryForm.resetFields(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to create category'); }
  });

  // Ticket Types
  const { data: ticketTypes = [], refetch: refetchTicketTypes } = useQuery('ticket-types', async () => {
    const res = await api.get('/api/ticket-types');
    return res.data?.types || [];
  }, { staleTime: 5 * 60 * 1000 });

  const createTicketType = useMutation(async (values: any) => {
    const res = await api.post('/api/ticket-types', values);
    return res.data;
  }, {
    onSuccess: () => { message.success('Ticket type created'); refetchTicketTypes(); ticketTypeForm.resetFields(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to create ticket type'); }
  });

  const updateTicketType = useMutation(async ({ id, ...values }: any) => {
    const res = await api.put(`/api/ticket-types/${id}`, values);
    return res.data;
  }, {
    onSuccess: () => { message.success('Ticket type updated'); refetchTicketTypes(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to update ticket type'); }
  });

  const deleteTicketType = useMutation(async (id: string) => {
    const res = await api.delete(`/api/ticket-types/${id}`);
    return res.data;
  }, {
    onSuccess: () => { message.success('Ticket type deleted'); refetchTicketTypes(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to delete ticket type'); }
  });

  const sortedCategories = useMemo(() => {
    const toOrder = (value: any, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);
    return [...categories].sort((a: any, b: any) => {
      const aOrder = toOrder(a.sort_order, Number.MAX_SAFE_INTEGER);
      const bOrder = toOrder(b.sort_order, Number.MAX_SAFE_INTEGER);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [categories]);

  const sortedTicketTypes = useMemo(() => {
    const toOrder = (value: any, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);
    return [...ticketTypes].sort((a: any, b: any) => {
      const aOrder = toOrder(a.sort_order, Number.MAX_SAFE_INTEGER);
      const bOrder = toOrder(b.sort_order, Number.MAX_SAFE_INTEGER);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [ticketTypes]);

  const moveCategoryOrder = async (categoryId: string, direction: 'up' | 'down') => {
    const current = [...sortedCategories];
    const fromIndex = current.findIndex((c: any) => c.id === categoryId);
    if (fromIndex < 0) return;

    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= current.length) return;

    const moved = [...current];
    const [item] = moved.splice(fromIndex, 1);
    moved.splice(toIndex, 0, item);

    const changed = moved
      .map((category: any, idx: number) => ({ id: category.id, sort_order: idx }))
      .filter((next: any, idx: number) => next.id !== current[idx]?.id || next.sort_order !== (current[idx]?.sort_order ?? idx));

    if (!changed.length) return;

    try {
      await Promise.all(
        changed.map((category: any) => api.put(`/api/categories/${category.id}`, { sort_order: category.sort_order }))
      );
      message.success('Category order updated');
      refetchCategories();
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to update category order');
    }
  };

  const moveTicketTypeOrder = async (typeId: string, direction: 'up' | 'down') => {
    const current = [...sortedTicketTypes];
    const fromIndex = current.findIndex((t: any) => t.id === typeId);
    if (fromIndex < 0) return;

    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= current.length) return;

    const moved = [...current];
    const [item] = moved.splice(fromIndex, 1);
    moved.splice(toIndex, 0, item);

    const changed = moved
      .map((type: any, idx: number) => ({ id: type.id, sort_order: idx }))
      .filter((next: any, idx: number) => next.id !== current[idx]?.id || next.sort_order !== (current[idx]?.sort_order ?? idx));

    if (!changed.length) return;

    try {
      await Promise.all(
        changed.map((type: any) => api.put(`/api/ticket-types/${type.id}`, { sort_order: type.sort_order }))
      );
      message.success('Ticket type order updated');
      refetchTicketTypes();
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to update ticket type order');
    }
  };

  // Services (admin-managed list; banner option for ticket display)
  const { data: servicesList = [], refetch: refetchServices } = useQuery('services', async () => {
    const res = await api.get('/api/services');
    return res.data?.services || [];
  });

  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceBanner, setNewServiceBanner] = useState(false);
  const [newServiceBannerColor, setNewServiceBannerColor] = useState('#1890ff');
  const [servicePendingEdits, setServicePendingEdits] = useState<Record<string, { name: string; show_banner: boolean; banner_color: string }>>({});

  const createService = useMutation(async (payload: { name: string; show_banner?: boolean; banner_color?: string }) => {
    const res = await api.post('/api/services', payload);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('Service added');
      refetchServices();
      setNewServiceName('');
      setNewServiceBanner(false);
      setNewServiceBannerColor('#1890ff');
    },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to add service'); }
  });

  const updateService = useMutation(async ({ id, ...payload }: { id: string; name?: string; show_banner?: boolean; banner_color?: string }) => {
    const res = await api.put(`/api/services/${id}`, payload);
    return res.data;
  }, {
    onSuccess: () => { message.success('Service updated'); refetchServices(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to update service'); }
  });

  const deleteService = useMutation(async (id: string) => {
    await api.delete(`/api/services/${id}`);
  }, {
    onSuccess: () => { message.success('Service removed'); refetchServices(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to remove service'); }
  });

  // API Tokens
  const { data: apiTokens = [], refetch: refetchApiTokens } = useQuery('api-tokens', async () => {
    const res = await api.get('/api/api-tokens');
    return res.data?.tokens || [];
  });

  const createApiToken = useMutation(async (values: any) => {
    const res = await api.post('/api/api-tokens', values);
    return res.data;
  }, {
    onSuccess: (data) => {
      message.success('API token created successfully');
      setNewTokenValue(data.token.token);
      setTokenModalVisible(true);
      // Invalidate and refetch to ensure UI updates
      queryClient.invalidateQueries('api-tokens');
      refetchApiTokens();
      apiTokenForm.resetFields();
    },
    onError: (e: any) => {
      const errorMessage = e.response?.data?.error || e.message || 'Failed to create API token';
      message.error(errorMessage);
    }
  });

  const revokeApiToken = useMutation(async (id: string) => {
    await api.delete(`/api/api-tokens/${id}`);
  }, {
    onSuccess: () => {
      message.success('API token revoked successfully');
      // Invalidate and refetch to ensure UI updates
      queryClient.invalidateQueries('api-tokens');
      refetchApiTokens();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to revoke API token');
    }
  });

  const deleteApiToken = useMutation(async (id: string) => {
    await api.delete(`/api/api-tokens/${id}?permanent=true`);
  }, {
    onSuccess: () => {
      message.success('API token deleted permanently');
      // Invalidate and refetch to ensure UI updates
      queryClient.invalidateQueries('api-tokens');
      refetchApiTokens();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to delete API token');
    }
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Token copied to clipboard');
  };

  // TV Dashboard Password
  const { data: tvDashboardPassword, refetch: refetchTvPassword } = useQuery('tv-dashboard-password', async () => {
    const res = await api.get('/api/settings/tv-dashboard-password?view=true');
    return res.data;
  }, {
    enabled: user?.role === 'admin'
  });

  const setTvDashboardPassword = useMutation(async (values: { password: string }) => {
    const res = await api.put('/api/settings/tv-dashboard-password', values);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('TV Dashboard password set successfully');
      refetchTvPassword();
      tvDashboardForm.resetFields();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to set TV Dashboard password');
    }
  });

  // TV Dashboard Announcement
  const { data: tvDashboardAnnouncement, refetch: refetchAnnouncement } = useQuery('tv-dashboard-announcement', async () => {
    const res = await api.get('/api/settings/tv-dashboard-announcement');
    return res.data;
  }, {
    enabled: user?.role === 'admin'
  });

  const setTvDashboardAnnouncement = useMutation(async (values: { announcement: string }) => {
    const res = await api.put('/api/settings/tv-dashboard-announcement', values);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('TV Dashboard announcement set successfully');
      refetchAnnouncement();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to set TV Dashboard announcement');
    }
  });

  const { data: tvDashboardNews = [], refetch: refetchTvNews } = useQuery('tv-dashboard-news-all', async () => {
    const res = await api.get('/api/settings/tv-dashboard-news/all');
    return (res.data?.news || []) as TvDashboardNewsItem[];
  }, {
    enabled: user?.role === 'admin'
  });

  const createTvNews = useMutation(async (values: { title: string; content: string; sortOrder?: number; isActive?: boolean }) => {
    const res = await api.post('/api/settings/tv-dashboard-news', values);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('News item created');
      refetchTvNews();
      tvNewsForm.resetFields();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to create news item');
    }
  });

  const updateTvNews = useMutation(async ({ id, ...values }: { id: string; title: string; content: string; sortOrder?: number; isActive?: boolean }) => {
    const res = await api.put(`/api/settings/tv-dashboard-news/${id}`, values);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('News item updated');
      refetchTvNews();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to update news item');
    }
  });

  const deleteTvNews = useMutation(async (id: string) => {
    await api.delete(`/api/settings/tv-dashboard-news/${id}`);
  }, {
    onSuccess: () => {
      message.success('News item deleted');
      refetchTvNews();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to delete news item');
    }
  });

  const openCreateTvNewsModal = () => {
    setEditingTvNews(null);
    tvNewsForm.resetFields();
    tvNewsForm.setFieldsValue({ sortOrder: 0, isActive: true });
    setTvNewsModalVisible(true);
  };

  const openEditTvNewsModal = (item: TvDashboardNewsItem) => {
    setEditingTvNews(item);
    tvNewsForm.setFieldsValue({
      title: item.title,
      content: item.content,
      sortOrder: item.sortOrder ?? 0,
      isActive: item.isActive !== false,
    });
    setTvNewsModalVisible(true);
  };

  const closeTvNewsModal = () => {
    setTvNewsModalVisible(false);
    setEditingTvNews(null);
    tvNewsForm.resetFields();
  };

  const handleSaveTvNews = (values: { title: string; content: string; sortOrder?: number; isActive?: boolean }) => {
    const payload = {
      title: String(values.title || '').trim(),
      content: String(values.content || '').trim(),
      sortOrder: values.sortOrder ?? 0,
      isActive: values.isActive !== false,
    };
    if (editingTvNews) {
      updateTvNews.mutate({ id: editingTvNews.id, ...payload }, { onSuccess: () => closeTvNewsModal() });
      return;
    }
    createTvNews.mutate(payload, { onSuccess: () => closeTvNewsModal() });
  };

  // TV Dashboard Call Me Alert
  const { data: tvCallMeAlert, refetch: refetchCallMeAlert } = useQuery('tv-dashboard-call-me-alert', async () => {
    const res = await api.get('/api/settings/tv-dashboard-call-me-alert');
    return res.data;
  }, { enabled: user?.role === 'admin' });

  const setTvCallMeAlert = useMutation(async (values: { enabled: boolean; interval: number; tone?: string }) => {
    const res = await api.put('/api/settings/tv-dashboard-call-me-alert', values);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('Call Me alert settings saved');
      refetchCallMeAlert();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to save');
    }
  });

  // Main App Call Me Bell Sound (Notification Bell)
  const { data: callMeBellSound, refetch: refetchCallMeBellSound } = useQuery('call-me-bell-sound-setting', async () => {
    const res = await api.get('/api/settings/call-me-bell-sound');
    return res.data;
  }, { enabled: user?.role === 'admin' });

  const setCallMeBellSound = useMutation(async (values: { enabled: boolean }) => {
    const res = await api.put('/api/settings/call-me-bell-sound', values);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('Call Me bell sound setting saved');
      refetchCallMeBellSound();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to save');
    }
  });

  // Custom ticket fields
  const { data: customFields = [], refetch: refetchFields } = useQuery('ticket-fields', async () => {
    const res = await api.get('/api/ticket-fields');
    return res.data?.fields || [];
  });

  const createField = useMutation(async (values: any) => {
    const res = await api.post('/api/ticket-fields', values);
    return res.data;
  }, {
    onSuccess: () => { message.success('Field created'); refetchFields(); fieldForm.resetFields(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to create field'); }
  });

  const updateField = useMutation(async ({ id, ...values }: any) => {
    const res = await api.put(`/api/ticket-fields/${id}`, values);
    return res.data;
  }, {
    onSuccess: () => { message.success('Field updated'); refetchFields(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to update field'); }
  });

  const deleteField = useMutation(async (id: string) => {
    const res = await api.delete(`/api/ticket-fields/${id}`);
    return res.data;
  }, {
    onSuccess: () => { message.success('Field deleted'); refetchFields(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to delete field'); }
  });

  const sortedCustomFields = useMemo(() => {
    const toOrder = (value: any, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);
    return [...customFields].sort((a: any, b: any) => {
      const aOrder = toOrder(a.sort_order, Number.MAX_SAFE_INTEGER);
      const bOrder = toOrder(b.sort_order, Number.MAX_SAFE_INTEGER);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.created_at || '').localeCompare(String(b.created_at || ''));
    });
  }, [customFields]);

  const moveFieldOrder = async (fieldId: string, direction: 'up' | 'down') => {
    const current = [...sortedCustomFields];
    const fromIndex = current.findIndex((f: any) => f.id === fieldId);
    if (fromIndex < 0) return;

    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= current.length) return;

    const moved = [...current];
    const [item] = moved.splice(fromIndex, 1);
    moved.splice(toIndex, 0, item);

    const changed = moved
      .map((field: any, idx: number) => ({ id: field.id, sort_order: idx }))
      .filter((next: any, idx: number) => next.id !== current[idx]?.id || next.sort_order !== (current[idx]?.sort_order ?? idx));

    if (!changed.length) return;

    try {
      await Promise.all(
        changed.map((field: any) => api.put(`/api/ticket-fields/${field.id}`, { sort_order: field.sort_order }))
      );
      message.success('Field order updated');
      refetchFields();
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to update field order');
    }
  };

  const parseMacroRules = (raw: string): MacroRule[] => {
    return String(raw || '')
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((pair) => {
        const idx = pair.indexOf('=');
        if (idx <= 0) return null;
        const field_key = pair.slice(0, idx).trim();
        const equals = pair.slice(idx + 1).trim();
        if (!field_key || !equals) return null;
        return { field_key, equals };
      })
      .filter(Boolean) as MacroRule[];
  };

  const stringifyMacroRules = (rules: MacroRule[] | undefined) =>
    (Array.isArray(rules) ? rules : [])
      .map((r) => `${r.field_key}=${r.equals}`)
      .join('; ');

  // Ticket Macros
  const { data: ticketMacros = [], refetch: refetchTicketMacros } = useQuery('ticket-macros-admin', async () => {
    const res = await api.get('/api/macros?include_inactive=true');
    return (res.data?.macros || []) as TicketMacro[];
  });

  const createTicketMacro = useMutation(async (values: any) => {
    const payload = {
      title: String(values.title || '').trim(),
      content: String(values.content || ''),
      apply_to_comment_type: values.apply_to_comment_type || 'both',
      is_active: values.is_active !== false,
      auto_apply: values.auto_apply === true,
      apply_on_new_ticket: values.apply_on_new_ticket === true,
      auto_apply_category_ids: Array.isArray(values.auto_apply_category_ids) ? values.auto_apply_category_ids : [],
      auto_apply_ticket_types: Array.isArray(values.auto_apply_ticket_types) ? values.auto_apply_ticket_types : [],
      auto_apply_custom_field_rules: parseMacroRules(values.custom_rules_text || ''),
    };
    const res = await api.post('/api/macros', payload);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('Macro created');
      refetchTicketMacros();
      queryClient.invalidateQueries('ticket-macros');
      macroForm.resetFields();
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to create macro');
    }
  });

  const updateTicketMacro = useMutation(async ({ id, ...values }: any) => {
    const payload = {
      title: String(values.title || '').trim(),
      content: String(values.content || ''),
      apply_to_comment_type: values.apply_to_comment_type || 'both',
      is_active: values.is_active !== false,
      auto_apply: values.auto_apply === true,
      apply_on_new_ticket: values.apply_on_new_ticket === true,
      auto_apply_category_ids: Array.isArray(values.auto_apply_category_ids) ? values.auto_apply_category_ids : [],
      auto_apply_ticket_types: Array.isArray(values.auto_apply_ticket_types) ? values.auto_apply_ticket_types : [],
      auto_apply_custom_field_rules: parseMacroRules(values.custom_rules_text || ''),
    };
    const res = await api.put(`/api/macros/${id}`, payload);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('Macro updated');
      refetchTicketMacros();
      queryClient.invalidateQueries('ticket-macros');
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to update macro');
    }
  });

  const deleteTicketMacro = useMutation(async (id: string) => {
    const res = await api.delete(`/api/macros/${id}`);
    return res.data;
  }, {
    onSuccess: () => {
      message.success('Macro deleted');
      refetchTicketMacros();
      queryClient.invalidateQueries('ticket-macros');
    },
    onError: (e: any) => {
      message.error(e.response?.data?.error || 'Failed to delete macro');
    }
  });

  const openCreateMacroModal = () => {
    setEditingMacro(null);
    macroForm.resetFields();
    macroForm.setFieldsValue({
      apply_to_comment_type: 'both',
      is_active: true,
      auto_apply: false,
      apply_on_new_ticket: false,
      auto_apply_category_ids: [],
      auto_apply_ticket_types: [],
      custom_rules_text: '',
    });
    setMacroModalVisible(true);
  };

  const openEditMacroModal = (macro: TicketMacro) => {
    setEditingMacro(macro);
    macroForm.setFieldsValue({
      title: macro.title,
      content: macro.content,
      apply_to_comment_type: macro.apply_to_comment_type || 'both',
      is_active: macro.is_active !== false,
      auto_apply: macro.auto_apply === true,
      apply_on_new_ticket: macro.apply_on_new_ticket === true,
      auto_apply_category_ids: Array.isArray(macro.auto_apply_category_ids) ? macro.auto_apply_category_ids : [],
      auto_apply_ticket_types: Array.isArray(macro.auto_apply_ticket_types) ? macro.auto_apply_ticket_types : [],
      custom_rules_text: stringifyMacroRules(macro.auto_apply_custom_field_rules),
    });
    setMacroModalVisible(true);
  };

  const closeMacroModal = () => {
    setMacroModalVisible(false);
    setEditingMacro(null);
    macroForm.resetFields();
  };

  const handleSaveMacro = (values: any) => {
    if (editingMacro) {
      updateTicketMacro.mutate(
        { id: editingMacro.id, ...values },
        { onSuccess: () => closeMacroModal() }
      );
      return;
    }
    createTicketMacro.mutate(values, { onSuccess: () => closeMacroModal() });
  };

  const toMacroPayload = (macro: TicketMacro, overrides: Partial<TicketMacro> = {}) => {
    const merged: TicketMacro = { ...macro, ...overrides } as TicketMacro;
    return {
      title: String(merged.title || '').trim(),
      content: String(merged.content || ''),
      apply_to_comment_type: merged.apply_to_comment_type || 'both',
      is_active: merged.is_active !== false,
      auto_apply: merged.auto_apply === true,
      apply_on_new_ticket: merged.apply_on_new_ticket === true,
      auto_apply_category_ids: Array.isArray(merged.auto_apply_category_ids) ? merged.auto_apply_category_ids : [],
      auto_apply_ticket_types: Array.isArray(merged.auto_apply_ticket_types) ? merged.auto_apply_ticket_types : [],
      auto_apply_custom_field_rules: Array.isArray(merged.auto_apply_custom_field_rules) ? merged.auto_apply_custom_field_rules : [],
    };
  };

  const runBulkMacroUpdate = async (targetIsActive: boolean) => {
    const selected = (ticketMacros || []).filter((m: TicketMacro) => selectedMacroRowKeys.includes(m.id));
    if (!selected.length) return;
    setMacroBulkLoading(true);
    try {
      await Promise.all(
        selected.map((macro: TicketMacro) =>
          api.put(`/api/macros/${macro.id}`, toMacroPayload(macro, { is_active: targetIsActive }))
        )
      );
      message.success(`Updated ${selected.length} macro${selected.length > 1 ? 's' : ''}`);
      setSelectedMacroRowKeys([]);
      refetchTicketMacros();
      queryClient.invalidateQueries('ticket-macros');
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to update selected macros');
    } finally {
      setMacroBulkLoading(false);
    }
  };

  const runBulkMacroDelete = async () => {
    const selected = (ticketMacros || []).filter((m: TicketMacro) => selectedMacroRowKeys.includes(m.id));
    if (!selected.length) return;
    setMacroBulkLoading(true);
    try {
      await Promise.all(selected.map((macro: TicketMacro) => api.delete(`/api/macros/${macro.id}`)));
      message.success(`Deleted ${selected.length} macro${selected.length > 1 ? 's' : ''}`);
      setSelectedMacroRowKeys([]);
      refetchTicketMacros();
      queryClient.invalidateQueries('ticket-macros');
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to delete selected macros');
    } finally {
      setMacroBulkLoading(false);
    }
  };

  const filteredMacros = (ticketMacros || []).filter((m: TicketMacro) => {
    const q = macroSearch.trim().toLowerCase();
    const searchMatch = !q || (
      String(m.title || '').toLowerCase().includes(q) ||
      String(m.content || '').toLowerCase().includes(q)
    );
    const typeMatch = macroFilterType === 'all' || m.apply_to_comment_type === macroFilterType;
    const autoMatch =
      macroFilterAutoApply === 'all' ||
      (macroFilterAutoApply === 'auto' ? m.auto_apply === true : m.auto_apply !== true);
    const statusMatch =
      macroFilterStatus === 'all' ||
      (macroFilterStatus === 'active' ? m.is_active !== false : m.is_active === false);
    return searchMatch && typeMatch && autoMatch && statusMatch;
  });

  const updateCategory = useMutation(async ({ id, ...values }: any) => {
    const res = await api.put(`/api/categories/${id}`, values);
    return res.data;
  }, {
    onSuccess: () => { message.success('Category updated'); refetchCategories(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to update category'); }
  });

  const deleteCategory = useMutation(async (id: string) => {
    const res = await api.delete(`/api/categories/${id}`);
    return res.data;
  }, {
    onSuccess: () => { message.success('Category deleted'); refetchCategories(); },
    onError: (e: any) => { message.error(e.response?.data?.error || 'Failed to delete category'); }
  });

  // Fetch settings
  const { data: settings, isLoading, error } = useQuery('settings', async () => {
      const response = await api.get('/api/settings');
      return response.data;
  });

  const { data: auditRolesData } = useQuery(
    'settings-ticket-audit-roles',
    async () => {
      const res = await api.get('/api/roles');
      return (res.data.roles || []).filter((r: { name: string }) => r.name !== 'admin');
    }
  );


  // Update system settings mutation
  const updateSystemSettings = useMutation(async (values: Partial<SystemSettings>) => {
    const response = await api.put('/api/settings/system', values);
      return response.data;
  }, {
      onSuccess: () => {
      message.success('System settings updated successfully');
        queryClient.invalidateQueries('settings');
      },
      onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update system settings');
    }
  });

  const updateTicketAuditSettings = useMutation(
    async (values: TicketAuditSettings) => {
      const response = await api.put('/api/settings/ticket-audit', {
        frequency: values.frequency,
        pool_size: values.poolSize,
        sample_size: values.sampleSize,
        target_roles: values.targetRoles,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Ticket audit settings saved');
        queryClient.invalidateQueries('settings');
        queryClient.invalidateQueries('ticket-audit-config');
        queryClient.invalidateQueries('ticket-audit-auditable-users');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to update ticket audit settings');
      },
    }
  );

  // Update email settings mutation
  const updateEmailSettings = useMutation(async (values: Partial<EmailSettings>) => {
    const response = await api.put('/api/settings/email', values);
      return response.data;
  }, {
      onSuccess: () => {
      message.success('Email settings updated successfully');
        queryClient.invalidateQueries('settings');
      },
      onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update email settings');
    }
  });

  // Test email mutation
  const testEmailMutation = useMutation(async (email: string) => {
      const response = await api.post('/api/settings/test-email', { email });
      return response.data;
  }, {
    onSuccess: () => {
      message.success('Test email sent successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to send test email');
    }
  });

  // Update RingCentral settings mutation
  const updateRingCentralSettings = useMutation(async (values: Partial<RingCentralSettings>) => {
    const response = await api.put('/api/settings/ringcentral', values);
    return response.data;
  }, {
    onSuccess: () => {
      message.success('RingCentral settings updated successfully');
      queryClient.invalidateQueries('settings');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update RingCentral settings');
    }
  });

  // Update business settings mutation
  const updateBusinessSettings = useMutation(async (values: Partial<BusinessSettings>) => {
    const response = await api.put('/api/settings/business', values);
    return response.data;
  }, {
    onSuccess: () => {
      message.success('Business settings updated successfully');
      queryClient.invalidateQueries('settings');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update business settings');
    }
  });


  // Load settings into forms when data is available
  useEffect(() => {
    if (settings) {
      systemForm.setFieldsValue(settings.system);
      emailForm.setFieldsValue(settings.email);
      
      // Handle email templates - ensure feedback template has values
      const emailTemplates = settings.emailTemplates || {};
      if (emailTemplates.feedback) {
        // If feedback template exists but is empty, it will be handled by backend defaults
        // But we can also check here and ensure it's not empty
        if (!emailTemplates.feedback.subject || !emailTemplates.feedback.bodyHtml) {
          // If empty, don't set it - let the form use initialValues from backend
          const { feedback, ...otherTemplates } = emailTemplates;
          templatesForm.setFieldsValue(otherTemplates);
        } else {
          templatesForm.setFieldsValue(emailTemplates);
        }
      } else {
        templatesForm.setFieldsValue(emailTemplates);
      }
      
      ringCentralForm.setFieldsValue(settings.ringcentral);
      uiForm.setFieldsValue(settings.ui);
      businessForm.setFieldsValue(settings.business);
      ticketAuditForm.setFieldsValue({
        frequency: settings.ticketAudit?.frequency || 'weekly',
        poolSize: settings.ticketAudit?.poolSize ?? 15,
        sampleSize: settings.ticketAudit?.sampleSize ?? 5,
        targetRoles: settings.ticketAudit?.targetRoles || ['agent', 'developer'],
      });
    }
  }, [settings, systemForm, emailForm, templatesForm, ringCentralForm, uiForm, businessForm, ticketAuditForm]);


  const handleSystemSave = (values: Partial<SystemSettings>) => {
    updateSystemSettings.mutate(values);
  };

  const handleEmailSave = (values: Partial<EmailSettings>) => {
    updateEmailSettings.mutate(values);
  };

  const updateEmailTemplates = useMutation(async (values: any) => {
    const response = await api.put('/api/settings/email-templates', values);
    return response.data;
  }, {
    onSuccess: () => {
      message.success('Email templates updated successfully');
      queryClient.invalidateQueries('settings');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update templates');
    }
  });

  const handleRingCentralSave = (values: Partial<RingCentralSettings>) => {
    updateRingCentralSettings.mutate(values);
  };

  const handleBusinessSave = (values: Partial<BusinessSettings>) => {
    updateBusinessSettings.mutate(values);
  };


  const handleTestEmail = () => {
    if (!testEmail) {
      message.error('Please enter an email address');
      return;
    }
    testEmailMutation.mutate(testEmail);
  };

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Alert message="Access Denied" description="Only administrators can access settings." type="warning" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert 
          message="Error loading settings" 
          description={(error as any)?.message || 'Failed to load settings'} 
          type="error" 
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Title level={2}>System Settings</Title>

      <Tabs defaultActiveKey="system" type="card">
        <TabPane tab={<span><SettingOutlined />System</span>} key="system">
          <Card title="System Configuration">
            <Form
              form={systemForm}
              layout="vertical"
              onFinish={handleSystemSave}
              initialValues={settings?.system}
            >
              {/* System Name and System Version fields hidden per user request */}

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="maxFileUploadSize"
                label="Max File Upload Size (MB)"
                    rules={[{ required: true, message: 'Please enter max file size' }]}
                  >
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sessionTimeout"
                label="Session Timeout (minutes)"
                    rules={[{ required: true, message: 'Please enter session timeout' }]}
                  >
                    <InputNumber min={30} max={1440} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              {/* Hidden system settings per user request */}

              <Form.Item style={{ marginTop: 24 }}>
            <Button
                  type="primary" 
                  icon={<SaveOutlined />} 
                  htmlType="submit"
                  loading={updateSystemSettings.isLoading}
            >
              Save System Settings
            </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab={<span><FileSearchOutlined />Ticket audit</span>} key="ticket-audit">
          <Card title="Ticket QA audits">
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Default period for the Ticket Audit page: weekly (last ISO week), bi-weekly (last 14 days), or
              monthly (last calendar month). Supervisors still run audits manually; this only sets the
              suggested date range.
            </Text>
            <Form
              form={ticketAuditForm}
              layout="vertical"
              onFinish={(v) => updateTicketAuditSettings.mutate(v as TicketAuditSettings)}
            >
              <Form.Item name="frequency" label="Default audit cadence" rules={[{ required: true, message: 'Select cadence' }]}>
                <Select style={{ maxWidth: 360 }}>
                  <Option value="weekly">Weekly (previous ISO week, Mon–Sun)</Option>
                  <Option value="biweekly">Bi-weekly (previous 14 days)</Option>
                  <Option value="monthly">Monthly (previous calendar month)</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="poolSize"
                label="Candidate pool size"
                extra="Recent tickets shown for the agent in the audit period before the auditor selects which to grade."
                rules={[{ required: true, message: 'Enter pool size' }]}
              >
                <InputNumber min={5} max={50} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item
                name="sampleSize"
                label="Tickets to audit per session"
                extra="Number of tickets the auditor must select and grade each session."
                rules={[
                  { required: true, message: 'Enter selection count' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const pool = getFieldValue('poolSize');
                      if (value == null || pool == null || value <= pool) return Promise.resolve();
                      return Promise.reject(new Error('Cannot exceed candidate pool size'));
                    },
                  }),
                ]}
              >
                <InputNumber min={1} max={50} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item
                name="targetRoles"
                label="Who can be audited (by role)"
                rules={[{ required: true, message: 'Select at least one role' }]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  style={{ maxWidth: 520 }}
                  placeholder="Roles shown in Ticket Audit agent dropdown (admin excluded)"
                  options={(auditRolesData || []).map((r: { name: string; display_name: string }) => ({
                    value: r.name,
                    label: `${r.display_name} (${r.name})`,
                  }))}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={updateTicketAuditSettings.isLoading}
                  icon={<SaveOutlined />}
                >
                  Save ticket audit settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab={<span><CustomerServiceOutlined />Services</span>} key="services">
          <Card
            title="Services"
            extra={
              <Text type="secondary">
                Services assigned to businesses. Enable &quot;Banner&quot; to show a large tag on tickets for that business.
              </Text>
            }
          >
            <Form
              layout="inline"
              onFinish={() => {
                if (newServiceName.trim()) {
                  createService.mutate({
                    name: newServiceName.trim(),
                    show_banner: newServiceBanner,
                    banner_color: newServiceBannerColor,
                  });
                }
              }}
              style={{ marginBottom: 16 }}
            >
              <Form.Item>
                <Input
                  placeholder="Service name (e.g. RetailZPOS)"
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  style={{ width: 200 }}
                />
              </Form.Item>
              <Form.Item>
                <span style={{ marginRight: 8, fontSize: 12, color: '#666' }}>Banner</span>
                <Switch size="small" checked={newServiceBanner} onChange={setNewServiceBanner} />
              </Form.Item>
              {newServiceBanner && (
                <Form.Item>
                  <span style={{ marginRight: 6, fontSize: 12, color: '#666' }}>Color</span>
                  <input
                    type="color"
                    value={newServiceBannerColor}
                    onChange={(e) => setNewServiceBannerColor(e.target.value)}
                    style={{ width: 28, height: 28, padding: 2, cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 4 }}
                    title="Banner background color"
                  />
                </Form.Item>
              )}
              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={createService.isLoading}
                >
                  Add Service
                </Button>
              </Form.Item>
            </Form>
            <div>
              {(servicesList as any[]).map((s: any) => {
                const pending = servicePendingEdits[s.id];
                const displayName = pending?.name ?? (s.name?.replace(/_/g, ' ') || s.name || '');
                const displayBanner = pending?.show_banner ?? !!s.show_banner;
                const displayColor = pending?.banner_color ?? (s.banner_color || '#1890ff');
                const hasChanges = pending !== undefined;
                return (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Input
                      value={displayName}
                      onChange={(e) =>
                        setServicePendingEdits((prev) => ({
                          ...prev,
                          [s.id]: {
                            name: e.target.value,
                            show_banner: prev[s.id]?.show_banner ?? !!s.show_banner,
                            banner_color: prev[s.id]?.banner_color ?? (s.banner_color || '#1890ff'),
                          },
                        }))
                      }
                      onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                      style={{ fontWeight: 500, width: 200 }}
                      placeholder="Service name"
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Switch
                        size="small"
                        checked={displayBanner}
                        onChange={(checked) =>
                          setServicePendingEdits((prev) => ({
                            ...prev,
                            [s.id]: {
                              name: prev[s.id]?.name ?? (s.name?.replace(/_/g, ' ') || ''),
                              show_banner: checked,
                              banner_color: prev[s.id]?.banner_color ?? (s.banner_color || '#1890ff'),
                            },
                          }))
                        }
                      />
                      <span style={{ fontSize: 12, color: '#666' }}>Banner</span>
                    </div>
                    {displayBanner && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#666' }}>Color</span>
                        <input
                          type="color"
                          value={displayColor}
                          onChange={(e) =>
                            setServicePendingEdits((prev) => ({
                              ...prev,
                              [s.id]: {
                                name: prev[s.id]?.name ?? (s.name?.replace(/_/g, ' ') || ''),
                                show_banner: prev[s.id]?.show_banner ?? !!s.show_banner,
                                banner_color: e.target.value,
                              },
                            }))
                          }
                          style={{ width: 28, height: 28, padding: 2, cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 4 }}
                          title="Banner background color"
                        />
                      </div>
                    )}
                    {hasChanges && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<SaveOutlined />}
                        loading={updateService.isLoading}
                        onClick={() => {
                          updateService.mutate(
                            { id: s.id, name: displayName.trim(), show_banner: displayBanner, banner_color: displayColor },
                            {
                              onSuccess: () => {
                                setServicePendingEdits((prev) => {
                                  const next = { ...prev };
                                  delete next[s.id];
                                  return next;
                                });
                              },
                            }
                          );
                        }}
                      >
                        Save
                      </Button>
                    )}
                    <Popconfirm
                      title="Remove this service? Businesses using it must have it removed first."
                      onConfirm={() => deleteService.mutate(s.id)}
                      okText="Remove"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Button danger size="small" icon={<DeleteOutlined />}>
                        Remove
                      </Button>
                    </Popconfirm>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabPane>

        <TabPane tab={<span><AppstoreOutlined />Ticket Fields</span>} key="ticket-fields">
          <Card title="Categories">
            <Form form={categoryForm} layout="inline" onFinish={(v) => createCategory.mutate(v)} style={{ marginBottom: 16 }}>
              <Form.Item name="name" rules={[{ required: true, message: 'Name required' }]}>
                <Input placeholder="Category name" />
              </Form.Item>
              <Form.Item name="description">
                <Input placeholder="Description" style={{ width: 300 }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={createCategory.isLoading}>Add</Button>
              </Form.Item>
            </Form>
            <div>
              {sortedCategories.map((c: any, index: number) => (
                <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Button
                      size="small"
                      icon={<UpOutlined />}
                      disabled={index === 0}
                      onClick={() => moveCategoryOrder(c.id, 'up')}
                    />
                    <Button
                      size="small"
                      icon={<DownOutlined />}
                      disabled={index === sortedCategories.length - 1}
                      onClick={() => moveCategoryOrder(c.id, 'down')}
                    />
                  </div>
                  <Input defaultValue={c.name} style={{ width: 200 }} onBlur={(e) => updateCategory.mutate({ id: c.id, name: e.target.value })} />
                  <Input defaultValue={c.description} style={{ width: 300 }} onBlur={(e) => updateCategory.mutate({ id: c.id, description: e.target.value })} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Switch 
                      checked={!!c.exclude_business_notifications} 
                      onChange={(checked) => updateCategory.mutate({ id: c.id, exclude_business_notifications: checked })}
                      size="small"
                    />
                    <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>Disable email notifications</span>
                  </div>
                  <Button danger onClick={() => deleteCategory.mutate(c.id)}>Delete</Button>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Ticket Types" style={{ marginTop: 16 }}>
            <Form form={ticketTypeForm} layout="inline" onFinish={(v) => createTicketType.mutate(v)} style={{ marginBottom: 16 }}>
              <Form.Item name="name" rules={[{ required: true, message: 'Name required' }]}>
                <Input placeholder="Type name" />
              </Form.Item>
              <Form.Item name="description">
                <Input placeholder="Description" style={{ width: 300 }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={createTicketType.isLoading}>Add</Button>
              </Form.Item>
            </Form>
            <div>
              {sortedTicketTypes.map((t: any, index: number) => (
                <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Button
                      size="small"
                      icon={<UpOutlined />}
                      disabled={index === 0}
                      onClick={() => moveTicketTypeOrder(t.id, 'up')}
                    />
                    <Button
                      size="small"
                      icon={<DownOutlined />}
                      disabled={index === sortedTicketTypes.length - 1}
                      onClick={() => moveTicketTypeOrder(t.id, 'down')}
                    />
                  </div>
                  <Input defaultValue={t.name} style={{ width: 200 }} onBlur={(e) => updateTicketType.mutate({ id: t.id, name: e.target.value })} />
                  <Input defaultValue={t.description} style={{ width: 300 }} onBlur={(e) => updateTicketType.mutate({ id: t.id, description: e.target.value })} />
                  <Switch checked={t.is_active !== false} onChange={(checked) => updateTicketType.mutate({ id: t.id, is_active: checked })} />
                  <Button danger onClick={() => deleteTicketType.mutate(t.id)}>Delete</Button>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Custom Ticket Fields" style={{ marginTop: 16 }}>
            <Form layout="inline" form={fieldForm} onFinish={(v) => createField.mutate(v)} style={{ marginBottom: 16 }}>
              <Form.Item name="field_key" rules={[{ required: true, message: 'Key required' }]}>
                <Input placeholder="Key (e.g., device_serial)" />
              </Form.Item>
              <Form.Item name="label" rules={[{ required: true, message: 'Label required' }]}>
                <Input placeholder="Label (e.g., Device Serial)" />
              </Form.Item>
              <Form.Item name="field_type" rules={[{ required: true, message: 'Type required' }]}>
                <Select placeholder="Type" style={{ width: 140 }}>
                  <Option value="text">Text</Option>
                  <Option value="number">Number</Option>
                  <Option value="checkbox">Checkbox</Option>
                  <Option value="select">Select</Option>
                </Select>
              </Form.Item>
              <Form.Item name="required" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
              <Form.Item name="options">
                <Input placeholder="Options (comma-separated for select)" style={{ width: 260 }} />
              </Form.Item>
              <Form.Item name="category_ids">
                <Select
                  mode="multiple"
                  placeholder="Categories"
                  allowClear
                  style={{ minWidth: 220 }}
                  maxTagCount="responsive"
                >
                  {categories.map((c: any) => (
                    <Option key={c.id} value={c.id}>{c.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={createField.isLoading}>Add Field</Button>
              </Form.Item>
            </Form>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
              `Active` controls whether the field appears in Agent Workspace. `Required` controls validation only.
            </Typography.Text>

            <div>
              {sortedCustomFields.map((f: any, index: number) => (
                <div key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Button
                      size="small"
                      icon={<UpOutlined />}
                      disabled={index === 0}
                      onClick={() => moveFieldOrder(f.id, 'up')}
                    />
                    <Button
                      size="small"
                      icon={<DownOutlined />}
                      disabled={index === sortedCustomFields.length - 1}
                      onClick={() => moveFieldOrder(f.id, 'down')}
                    />
                  </div>
                  <Input defaultValue={f.field_key} style={{ width: 180 }} onBlur={(e) => updateField.mutate({ id: f.id, field_key: e.target.value })} />
                  <Input defaultValue={f.label} style={{ width: 200 }} onBlur={(e) => updateField.mutate({ id: f.id, label: e.target.value })} />
                  <Select defaultValue={f.field_type} style={{ width: 140 }} onChange={(v) => updateField.mutate({ id: f.id, field_type: v })}>
                    <Option value="text">Text</Option>
                    <Option value="number">Number</Option>
                    <Option value="checkbox">Checkbox</Option>
                    <Option value="select">Select</Option>
                  </Select>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Switch checked={f.is_active !== false} onChange={(v) => updateField.mutate({ id: f.id, is_active: v })} />
                    <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>Active</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Switch checked={!!f.required} onChange={(v) => updateField.mutate({ id: f.id, required: v })} />
                    <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>Required</span>
                  </div>
                  <Input defaultValue={(Array.isArray(f.options) ? f.options : [])?.join?.(', ')} style={{ width: 260 }} onBlur={(e) => updateField.mutate({ id: f.id, options: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean) })} />
                  <Select
                    mode="multiple"
                    style={{ minWidth: 220 }}
                    placeholder="Categories"
                    allowClear
                    maxTagCount="responsive"
                    value={Array.isArray(f.category_ids) ? f.category_ids : []}
                    onChange={(vals) => updateField.mutate({ id: f.id, category_ids: vals })}
                  >
                    {categories.map((c: any) => (
                      <Option key={c.id} value={c.id}>{c.name}</Option>
                    ))}
                  </Select>
                  <Button danger onClick={() => deleteField.mutate(f.id)}>Delete</Button>
                </div>
              ))}
            </div>
          </Card>
        </TabPane>

        <TabPane tab={<span><TableOutlined />Ticket Macros</span>} key="ticket-macros">
          <Card
            title="Ticket Macros"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateMacroModal}>
                New Macro
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Input
                  placeholder="Search macros by name or content..."
                  value={macroSearch}
                  onChange={(e) => setMacroSearch(e.target.value)}
                  allowClear
                  style={{ minWidth: 280, maxWidth: 420 }}
                />
                <Space wrap>
                  <Select
                    value={macroFilterType}
                    style={{ width: 170 }}
                    onChange={(v) => setMacroFilterType(v)}
                    options={[
                      { value: 'all', label: 'All comment types' },
                      { value: 'both', label: 'Public + Internal' },
                      { value: 'public', label: 'Public' },
                      { value: 'internal', label: 'Internal' },
                    ]}
                  />
                  <Select
                    value={macroFilterAutoApply}
                    style={{ width: 150 }}
                    onChange={(v) => setMacroFilterAutoApply(v)}
                    options={[
                      { value: 'all', label: 'All auto modes' },
                      { value: 'auto', label: 'Auto apply' },
                      { value: 'manual', label: 'Manual only' },
                    ]}
                  />
                  <Select
                    value={macroFilterStatus}
                    style={{ width: 130 }}
                    onChange={(v) => setMacroFilterStatus(v)}
                    options={[
                      { value: 'all', label: 'All status' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                  />
                  <Button
                    onClick={() => {
                      setMacroSearch('');
                      setMacroFilterType('all');
                      setMacroFilterAutoApply('all');
                      setMacroFilterStatus('all');
                    }}
                  >
                    Clear
                  </Button>
                </Space>
              </Space>
              <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text type="secondary">
                  {filteredMacros.length} macro{filteredMacros.length !== 1 ? 's' : ''} found
                </Text>
                <Space wrap>
                  <Button
                    disabled={selectedMacroRowKeys.length === 0}
                    loading={macroBulkLoading}
                    onClick={() => runBulkMacroUpdate(true)}
                  >
                    Activate Selected
                  </Button>
                  <Button
                    disabled={selectedMacroRowKeys.length === 0}
                    loading={macroBulkLoading}
                    onClick={() => runBulkMacroUpdate(false)}
                  >
                    Deactivate Selected
                  </Button>
                  <Popconfirm
                    title={`Delete ${selectedMacroRowKeys.length} selected macro${selectedMacroRowKeys.length !== 1 ? 's' : ''}?`}
                    onConfirm={runBulkMacroDelete}
                    disabled={selectedMacroRowKeys.length === 0}
                    okButtonProps={{ danger: true }}
                  >
                    <Button danger disabled={selectedMacroRowKeys.length === 0} loading={macroBulkLoading}>
                      Delete Selected
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>
              <Table
                rowKey="id"
                dataSource={filteredMacros}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                rowSelection={{
                  selectedRowKeys: selectedMacroRowKeys,
                  onChange: (keys) => setSelectedMacroRowKeys(keys),
                  preserveSelectedRowKeys: true,
                }}
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'title',
                    key: 'title',
                    width: 240,
                    render: (value: string, record: TicketMacro) => (
                      <Space direction="vertical" size={0}>
                        <Text strong>{value}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {(record.content || '').replace(/<[^>]*>/g, '').slice(0, 90)}{(record.content || '').length > 90 ? '...' : ''}
                        </Text>
                      </Space>
                    ),
                  },
                  {
                    title: 'Applies To',
                    dataIndex: 'apply_to_comment_type',
                    key: 'apply_to_comment_type',
                    width: 120,
                    render: (v: string) => (
                      <Tag color={v === 'both' ? 'blue' : v === 'public' ? 'green' : 'orange'}>
                        {v === 'both' ? 'Public + Internal' : v === 'public' ? 'Public' : 'Internal'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Auto Apply',
                    dataIndex: 'auto_apply',
                    key: 'auto_apply',
                    width: 100,
                    render: (v: boolean) => (v ? <Tag color="purple">Yes</Tag> : <Tag>No</Tag>),
                  },
                  {
                    title: 'New ticket',
                    dataIndex: 'apply_on_new_ticket',
                    key: 'apply_on_new_ticket',
                    width: 100,
                    render: (v: boolean) => (v ? <Tag color="cyan">Yes</Tag> : <Tag>No</Tag>),
                  },
                  {
                    title: 'Conditions',
                    key: 'conditions',
                    render: (_: any, record: TicketMacro) => {
                      const cCount = Array.isArray(record.auto_apply_category_ids) ? record.auto_apply_category_ids.length : 0;
                      const tCount = Array.isArray(record.auto_apply_ticket_types) ? record.auto_apply_ticket_types.length : 0;
                      const rCount = Array.isArray(record.auto_apply_custom_field_rules) ? record.auto_apply_custom_field_rules.length : 0;
                      return (
                        <Text type="secondary">
                          Categories: {cCount} | Types: {tCount} | Rules: {rCount}
                        </Text>
                      );
                    },
                  },
                  {
                    title: 'Status',
                    dataIndex: 'is_active',
                    key: 'is_active',
                    width: 100,
                    render: (v: boolean, record: TicketMacro) => (
                      <Switch
                        checked={v}
                        onChange={(checked) =>
                          updateTicketMacro.mutate({
                            id: record.id,
                            title: record.title,
                            content: record.content,
                            apply_to_comment_type: record.apply_to_comment_type,
                            is_active: checked,
                            auto_apply: record.auto_apply,
                            apply_on_new_ticket: record.apply_on_new_ticket === true,
                            auto_apply_category_ids: Array.isArray(record.auto_apply_category_ids) ? record.auto_apply_category_ids : [],
                            auto_apply_ticket_types: Array.isArray(record.auto_apply_ticket_types) ? record.auto_apply_ticket_types : [],
                            custom_rules_text: stringifyMacroRules(record.auto_apply_custom_field_rules),
                          })
                        }
                      />
                    ),
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    width: 160,
                    render: (_: any, record: TicketMacro) => (
                      <Space>
                        <Button size="small" onClick={() => openEditMacroModal(record)}>Edit</Button>
                        <Popconfirm title="Delete this macro?" onConfirm={() => deleteTicketMacro.mutate(record.id)}>
                          <Button danger size="small">Delete</Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
            </Space>
          </Card>

          <Modal
            title={editingMacro ? 'Edit Macro' : 'New Macro'}
            open={macroModalVisible}
            onCancel={closeMacroModal}
            onOk={() => macroForm.submit()}
            okText={editingMacro ? 'Save' : 'Create'}
            confirmLoading={createTicketMacro.isLoading || updateTicketMacro.isLoading}
            width={900}
          >
            <Form form={macroForm} layout="vertical" onFinish={handleSaveMacro}>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="title" label="Macro Name" rules={[{ required: true, message: 'Macro name is required' }]}>
                    <Input placeholder="e.g. Payment Processor Escalation" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="apply_to_comment_type" label="Applies To">
                    <Select>
                      <Option value="both">Public + Internal</Option>
                      <Option value="public">Public only</Option>
                      <Option value="internal">Internal only</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="is_active" label="Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="auto_apply" label="Auto Apply" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="apply_on_new_ticket"
                label="Apply when a new ticket is created"
                valuePropName="checked"
                tooltip="Runs on every new ticket (API create, Call Me convert, replacement tickets, follow-ups). Public / both: text is appended to the ticket description. Internal only: posted as the first internal comment."
              >
                <Switch />
              </Form.Item>
              <Form.Item name="content" label="Macro Content" rules={[{ required: true, message: 'Macro content is required' }]}>
                <Input.TextArea rows={5} placeholder="Text/HTML inserted into comment box" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="auto_apply_category_ids" label="Auto categories">
                    <Select mode="multiple" allowClear placeholder="Match category IDs">
                      {categories.map((c: any) => (
                        <Option key={c.id} value={c.id}>{c.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="auto_apply_ticket_types" label="Auto ticket types">
                    <Select mode="multiple" allowClear placeholder="Match ticket type names">
                      {ticketTypes.filter((t: any) => t.is_active !== false).map((t: any) => (
                        <Option key={t.id} value={t.name}>{t.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="custom_rules_text"
                    label="Auto custom fields"
                    tooltip="Use field_key=value pairs separated by semicolons. Example: problem_type=network; severity=high"
                  >
                    <Input placeholder="problem_type=network; severity=high" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>
        </TabPane>

        <TabPane tab={<span><MailOutlined />Email</span>} key="email">
          <Card title="Email Configuration" extra={<Text type="secondary">SMTP settings are permanently configured</Text>}>
            <Form
              form={emailForm}
              layout="vertical"
              onFinish={handleEmailSave}
              initialValues={settings?.email}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="smtpHost"
                    label="SMTP Host"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="smtpPort"
                    label="SMTP Port"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="smtpUsername"
                    label="SMTP Username"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="smtpPassword"
                    label="SMTP Password"
                  >
                    <Input.Password disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="fromEmail"
                    label="From Email"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="fromName"
                    label="From Name"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Business Notifications</Text>
                    <div><Text type="secondary">Send notifications to businesses</Text></div>
                  </div>
                  <Form.Item name="enableBusinessNotifications" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Agent Notifications</Text>
                    <div><Text type="secondary">Send notifications to agents</Text></div>
                  </div>
                  <Form.Item name="enableAgentNotifications" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>System Alerts</Text>
                    <div><Text type="secondary">Send system alert emails</Text></div>
                  </div>
                  <Form.Item name="enableSystemAlerts" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Feedback Requests</Text>
                    <div><Text type="secondary">Send feedback request emails when tickets are solved</Text></div>
                  </div>
                  <Form.Item name="enableFeedbackRequests" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>
              </Space>

              <Divider />

              <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <Text strong>Test Email Configuration</Text>
                <div style={{ marginTop: 8 }}>
                  <Input
                    placeholder="Enter email address to test"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    style={{ width: 300, marginRight: 8 }}
                  />
            <Button
                    icon={<SendOutlined />} 
                    onClick={handleTestEmail}
                    loading={testEmailMutation.isLoading}
                  >
                    Send Test Email
            </Button>
                </div>
              </div>

              <Form.Item>
            <Button
                  type="primary" 
                  icon={<SaveOutlined />} 
                  htmlType="submit"
                  loading={updateEmailSettings.isLoading}
                >
                  Save Notification Settings
            </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card 
            title={
              <span
                onClick={() => {
                  const newCount = emailTemplateClickCount + 1;
                  setEmailTemplateClickCount(newCount);
                  if (newCount >= 5) {
                    setResetAllModalVisible(true);
                    setEmailTemplateClickCount(0); // Reset counter
                  }
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Email Templates
              </span>
            }
            style={{ marginTop: 16 }}
          >
            <Form form={templatesForm} layout="vertical" onFinish={(v) => updateEmailTemplates.mutate(v)} initialValues={settings?.emailTemplates}>
              <Tabs defaultActiveKey="businessComment" type="card">
                <TabPane tab="Business Comment" key="businessComment">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>Business Comment Template</Typography.Title>
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleResetEmailTemplate('businessComment', 'Business Comment')}
                    >
                      Reset to Default
                    </Button>
                  </div>
                  <Form.Item name={["businessComment","subject"]} label="Subject">
                    <Input placeholder="Subject with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item name={["businessComment","bodyHtml"]} label="HTML Body">
                    <Input.TextArea rows={12} placeholder="HTML with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewVisible({ ...previewVisible, businessComment: !previewVisible.businessComment })}
                      style={{ marginBottom: 16 }}
                    >
                      {previewVisible.businessComment ? 'Hide Preview' : 'Show Preview'}
                    </Button>
                    {previewVisible.businessComment && (
                      <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, background: '#fff', minHeight: 120, marginTop: 8 }}
                           dangerouslySetInnerHTML={{ __html: (templatesForm.getFieldValue(["businessComment","bodyHtml"])) || '' }} />
                    )}
                  </Form.Item>
                </TabPane>
                
                <TabPane tab="Follower Comment" key="followerComment">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>Follower Comment Template</Typography.Title>
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleResetEmailTemplate('followerComment', 'Follower Comment')}
                    >
                      Reset to Default
                    </Button>
                  </div>
                  <Form.Item name={["followerComment","subject"]} label="Subject">
                    <Input placeholder="Subject with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item name={["followerComment","bodyHtml"]} label="HTML Body">
                    <Input.TextArea rows={12} placeholder="HTML with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewVisible({ ...previewVisible, followerComment: !previewVisible.followerComment })}
                      style={{ marginBottom: 16 }}
                    >
                      {previewVisible.followerComment ? 'Hide Preview' : 'Show Preview'}
                    </Button>
                    {previewVisible.followerComment && (
                      <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, background: '#fff', minHeight: 120, marginTop: 8 }}
                           dangerouslySetInnerHTML={{ __html: (templatesForm.getFieldValue(["followerComment","bodyHtml"])) || '' }} />
                    )}
                  </Form.Item>
                </TabPane>
                
                <TabPane tab="Agent Assignment" key="agentAssignment">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>Agent Assignment Template</Typography.Title>
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleResetEmailTemplate('agentAssignment', 'Agent Assignment')}
                    >
                      Reset to Default
                    </Button>
                  </div>
                  <Form.Item name={["agentAssignment","subject"]} label="Subject">
                    <Input placeholder="Subject with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item name={["agentAssignment","bodyHtml"]} label="HTML Body">
                    <Input.TextArea rows={12} placeholder="HTML with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewVisible({ ...previewVisible, agentAssignment: !previewVisible.agentAssignment })}
                      style={{ marginBottom: 16 }}
                    >
                      {previewVisible.agentAssignment ? 'Hide Preview' : 'Show Preview'}
                    </Button>
                    {previewVisible.agentAssignment && (
                      <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, background: '#fff', minHeight: 120, marginTop: 8 }}
                           dangerouslySetInnerHTML={{ __html: (templatesForm.getFieldValue(["agentAssignment","bodyHtml"])) || '' }} />
                    )}
                  </Form.Item>
                </TabPane>
                
                <TabPane tab="Agent Escalation" key="agentEscalation">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>Agent Escalation Template</Typography.Title>
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleResetEmailTemplate('agentEscalation', 'Agent Escalation')}
                    >
                      Reset to Default
                    </Button>
                  </div>
                  <Form.Item name={["agentEscalation","subject"]} label="Subject">
                    <Input placeholder="Subject with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item name={["agentEscalation","bodyHtml"]} label="HTML Body">
                    <Input.TextArea rows={12} placeholder="HTML with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewVisible({ ...previewVisible, agentEscalation: !previewVisible.agentEscalation })}
                      style={{ marginBottom: 16 }}
                    >
                      {previewVisible.agentEscalation ? 'Hide Preview' : 'Show Preview'}
                    </Button>
                    {previewVisible.agentEscalation && (
                      <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, background: '#fff', minHeight: 120, marginTop: 8 }}
                           dangerouslySetInnerHTML={{ __html: (templatesForm.getFieldValue(["agentEscalation","bodyHtml"])) || '' }} />
                    )}
                  </Form.Item>
                </TabPane>
                
                <TabPane tab="System Alert" key="systemAlert">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>System Alert Template</Typography.Title>
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleResetEmailTemplate('systemAlert', 'System Alert')}
                    >
                      Reset to Default
                    </Button>
                  </div>
                  <Form.Item name={["systemAlert","subject"]} label="Subject">
                    <Input placeholder="Subject with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item name={["systemAlert","bodyHtml"]} label="HTML Body">
                    <Input.TextArea rows={12} placeholder="HTML with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewVisible({ ...previewVisible, systemAlert: !previewVisible.systemAlert })}
                      style={{ marginBottom: 16 }}
                    >
                      {previewVisible.systemAlert ? 'Hide Preview' : 'Show Preview'}
                    </Button>
                    {previewVisible.systemAlert && (
                      <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, background: '#fff', minHeight: 120, marginTop: 8 }}
                           dangerouslySetInnerHTML={{ __html: (templatesForm.getFieldValue(["systemAlert","bodyHtml"])) || '' }} />
                    )}
                  </Form.Item>
                </TabPane>
                
                <TabPane tab="Feedback" key="feedback">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>Feedback Request Template</Typography.Title>
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleResetEmailTemplate('feedback', 'Feedback')}
                    >
                      Reset to Default
                    </Button>
                  </div>
                  <Form.Item name={["feedback","subject"]} label="Subject">
                    <Input placeholder="Subject with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item name={["feedback","bodyHtml"]} label="HTML Body">
                    <Input.TextArea rows={12} placeholder="HTML with {{placeholders}}" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setPreviewVisible({ ...previewVisible, feedback: !previewVisible.feedback })}
                      style={{ marginBottom: 16 }}
                    >
                      {previewVisible.feedback ? 'Hide Preview' : 'Show Preview'}
                    </Button>
                    {previewVisible.feedback && (
                      <div style={{ border: '1px solid #eee', borderRadius: 6, padding: 12, background: '#fff', minHeight: 120, marginTop: 8 }}
                           dangerouslySetInnerHTML={{ __html: (templatesForm.getFieldValue(["feedback","bodyHtml"]) || settings?.emailTemplates?.feedback?.bodyHtml || '') }} />
                    )}
                  </Form.Item>
                </TabPane>
              </Tabs>
              
              {/* Placeholder Guide Section */}
              <Collapse 
                style={{ marginTop: 16 }}
                items={[
                  {
                    key: '1',
                    label: (
                      <Space>
                        <InfoCircleOutlined />
                        <strong>Available Email Placeholders - Click to View All</strong>
                      </Space>
                    ),
                    children: (
                      <div>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          {/* Download Guide Button */}
                          <Alert 
                            message="Need a comprehensive guide?" 
                            description={
                              <Space>
                                <span>Download the complete Email Template Placeholders guide with examples and best practices.</span>
                                <Button 
                                  type="link" 
                                  icon={<DownloadOutlined />}
                                  onClick={async () => {
                                    try {
                                      // Fetch the file from the backend API
                                      const response = await api.get('/email-template-guide', {
                                        responseType: 'blob'
                                      });
                                      
                                      // Create a temporary download link
                                      const blob = new Blob([response.data], { type: 'text/markdown' });
                                      const url = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = 'Email_Template_Placeholders_Guide.md';
                                      document.body.appendChild(link);
                                      link.click();
                                      
                                      // Cleanup
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(url);
                                      
                                      notification.success({
                                        message: 'Guide Downloaded',
                                        description: 'Email Template Placeholders guide has been downloaded successfully.'
                                      });
                                    } catch (error) {
                                      console.error('Download error:', error);
                                      notification.error({
                                        message: 'Download Failed',
                                        description: 'Failed to download the guide. Please try again.'
                                      });
                                    }
                                  }}
                                >
                                  Download Guide
                                </Button>
                              </Space>
                            }
                            type="info" 
                            showIcon
                            icon={<FileTextOutlined />}
                          />

                          {/* Ticket Information */}
                          <div>
                            <Typography.Title level={5}>
                              <Tag color="blue">Ticket Information</Tag>
                            </Typography.Title>
                            <Descriptions bordered size="small" column={1}>
                              <Descriptions.Item label={<code>{'{{ticket_number}}'}</code>}>
                                Unique ticket number (e.g., TKT-12345)
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{subject}}'}</code>}>
                                Ticket subject/title
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{comment}}'}</code>}>
                                Latest comment/update content
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{comment_author}}'}</code>}>
                                Name of the person who wrote the comment (same value as {'{{agent_name}}'} for ticket updates)
                              </Descriptions.Item>
                            </Descriptions>
                          </div>

                          {/* Agent Information */}
                          <div>
                            <Typography.Title level={5}>
                              <Tag color="green">Agent Information</Tag>
                            </Typography.Title>
                            <Descriptions bordered size="small" column={1}>
                              <Descriptions.Item label={<code>{'{{agent_name}}'}</code>}>
                                Name of the agent who added the comment (alias: {'{{comment_author}}'})
                              </Descriptions.Item>
                            </Descriptions>
                          </div>

                          {/* Business Information */}
                          <div>
                            <Typography.Title level={5}>
                              <Tag color="orange">Business Information</Tag>
                            </Typography.Title>
                            <Descriptions bordered size="small" column={1}>
                              <Descriptions.Item label={<code>{'{{business_name}}'}</code>}>
                                Business/company name
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{mid}}'}</code>}>
                                Merchant ID (shows "N/A" if empty)
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_address}}'}</code>}>
                                Street address
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_city}}'}</code>}>
                                City
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_state}}'}</code>}>
                                State
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_zip_code}}'}</code>}>
                                ZIP/Postal code
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_phone}}'}</code>}>
                                Business phone number
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_email}}'}</code>}>
                                Business email address
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_contact_name}}'}</code>}>
                                Primary contact person name
                              </Descriptions.Item>
                              <Descriptions.Item label={<code>{'{{business_type}}'}</code>}>
                                Type/category of business
                              </Descriptions.Item>
                            </Descriptions>
                          </div>

                          {/* Follower Information */}
                          <div>
                            <Typography.Title level={5}>
                              <Tag color="purple">Follower Information</Tag>
                            </Typography.Title>
                            <Descriptions bordered size="small" column={1}>
                              <Descriptions.Item label={<code>{'{{follower_name}}'}</code>}>
                                Name of the person receiving the email (for follower notifications)
                              </Descriptions.Item>
                            </Descriptions>
                          </div>

                          {/* Usage Example */}
                          <Alert
                            message="Usage Example"
                            description={
                              <div>
                                <p><strong>In your template, use:</strong></p>
                                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, overflow: 'auto' }}>
{`<p>Hello {{business_name}},</p>
<p>Your store at {{business_address}}, {{business_city}} 
has a ticket update:</p>
<p><strong>Comment from {{comment_author}}</strong></p>
<p>{{comment}}</p>
<p>Contact us at {{business_phone}}</p>
<p>Thanks, {{agent_name}}</p>`}
                                </pre>
                                <Typography.Text type="secondary">
                                  Note: Empty placeholders will show as blank. All placeholders are case-sensitive.
                                </Typography.Text>
                              </div>
                            }
                            type="success"
                            showIcon
                          />
                        </Space>
                      </div>
                    ),
                  },
                ]}
              />
              
              <Form.Item style={{ marginTop: 16 }}>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={updateEmailTemplates.isLoading}>Save Templates</Button>
              </Form.Item>
              
              {/* Easter Egg: Reset All Templates Modal */}
              <Modal
                title="Reset All Email Templates?"
                open={resetAllModalVisible}
                onOk={async () => {
                  try {
                    await api.post('/api/settings/email-templates/reset', {});
                    message.success('All templates reset to defaults');
                    queryClient.invalidateQueries('settings');
                    setResetAllModalVisible(false);
                  } catch (e: any) {
                    message.error(e?.response?.data?.error || 'Failed to reset templates');
                    setResetAllModalVisible(false);
                  }
                }}
                onCancel={() => setResetAllModalVisible(false)}
                okText="Yes, Reset All"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <p>Are you sure you want to reset all email templates to their default values?</p>
                <p style={{ color: '#ff4d4f', fontWeight: 500, marginTop: 12 }}>
                  ⚠️ This action cannot be undone. All custom template changes will be lost.
                </p>
              </Modal>
              
              <Form.Item label="Send Test Email">
                <Space>
                  <Input placeholder="recipient@example.com" style={{ width: 260 }} id="testEmailTo" />
                  <Button icon={<SendOutlined />} onClick={async () => {
                    const to = (document.getElementById('testEmailTo') as HTMLInputElement)?.value;
                    if (!to) { message.error('Enter recipient email'); return; }
                    const current = templatesForm.getFieldsValue();
                    try {
                      await api.post('/api/settings/email-templates/test', {
                        to,
                        template: 'businessComment',
                        vars: { ticket_number: '10001', subject: 'Sample Ticket', comment: 'Sample public comment', agent_name: 'Admin User', comment_author: 'Admin User', business_name: 'Sample Biz', mid: '123456' },
                        override: current.businessComment,
                      });
                      message.success('Test email sent');
                    } catch (e: any) {
                      message.error(e?.response?.data?.error || 'Failed to send test email');
                    }
                  }}>Send Test</Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab={<span><PhoneOutlined />RingCentral</span>} key="ringcentral">
          <Card title="RingCentral Configuration">
            <Form
              form={ringCentralForm}
              layout="vertical"
              onFinish={handleRingCentralSave}
              initialValues={settings?.ringcentral}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="server"
                    label="RingCentral Server"
                    rules={[{ required: true, message: 'Please enter RingCentral server URL' }]}
                  >
                    <Input placeholder="https://platform.ringcentral.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="clientId"
                    label="Client ID"
                    rules={[{ required: true, message: 'Please enter client ID' }]}
                  >
                    <Input placeholder="Enter RingCentral client ID" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="clientSecret"
                    label="Client Secret"
                    rules={[{ required: true, message: 'Please enter client secret' }]}
                  >
                    <Input.Password placeholder="Enter RingCentral client secret" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="redirectUri"
                    label="Redirect URI"
                    rules={[{ required: true, message: 'Please enter redirect URI' }]}
                  >
                    <Input placeholder="http://patelhrms.com/ringcentral/callback" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="webhookUrl"
                    label="Webhook URL"
                    rules={[{ required: true, message: 'Please enter webhook URL' }]}
                  >
                    <Input placeholder="https://patelhrms.com/api/ringcentral/webhook" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="defaultCallerId"
                    label="Default Caller ID"
                  >
                    <Input placeholder="Enter default caller ID" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Enable Call Logging</Text>
                    <div><Text type="secondary">Log all incoming and outgoing calls</Text></div>
                  </div>
                  <Form.Item name="enableCallLogging" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Enable Call Recording</Text>
                    <div><Text type="secondary">Record calls for quality assurance</Text></div>
                  </div>
                  <Form.Item name="enableCallRecording" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>
              </Space>

              <Form.Item style={{ marginTop: 24 }}>
            <Button
                  type="primary" 
                  icon={<SaveOutlined />} 
                  htmlType="submit"
                  loading={updateRingCentralSettings.isLoading}
                >
                  Save RingCentral Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>


        {/* UI Preferences tab hidden */}

        {/* Business tab hidden */}
        {false && (
          <TabPane tab={<span><UserOutlined />Business</span>} key="business">
          <Card title="Business Configuration">
            <Form
              form={businessForm}
              layout="vertical"
              onFinish={handleBusinessSave}
              initialValues={settings?.business}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="defaultCountry"
                    label="Default Country"
                    rules={[{ required: true, message: 'Please enter default country' }]}
                  >
                    <Input placeholder="USA" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="serviceTypes"
                    label="Available Service Types"
                  >
                    <Select mode="multiple" placeholder="Select service types">
                      <Option value="retailzpos">RetailZPOS</Option>
                      <Option value="ptechpos">PTechPOS</Option>
                      <Option value="moolah_points">Moolah Points</Option>
                      <Option value="patelhms">Patel HMS</Option>
                      <Option value="credit_card_processing">Credit Card Processing</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="equipmentTypes"
                    label="Available Equipment Types"
                  >
                    <Select mode="multiple" placeholder="Select equipment types">
                      <Option value="pos_terminal">POS Terminal</Option>
                      <Option value="printer">Printer</Option>
                      <Option value="scanner">Scanner</Option>
                      <Option value="router">Router</Option>
                      <Option value="tablet">Tablet</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Require MID</Text>
                    <div><Text type="secondary">Make Merchant ID required for all businesses</Text></div>
                  </div>
                  <Form.Item name="requireMID" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Enable Portal</Text>
                    <div><Text type="secondary">Show portal status in business profiles</Text></div>
                  </div>
                  <Form.Item name="enableRetailZPOSPortal" valuePropName="checked" style={{ margin: 0 }}>
                    <Switch />
                  </Form.Item>
                </div>
              </Space>

              <Form.Item style={{ marginTop: 24 }}>
          <Button
                  type="primary" 
                  icon={<SaveOutlined />} 
                  htmlType="submit"
                  loading={updateBusinessSettings.isLoading}
                >
                  Save Business Settings
          </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
        )}
        
        {/* API Tokens Tab */}
        <TabPane tab={<span><KeyOutlined />API Tokens</span>} key="api-tokens">
          <Card 
            title="API Token Management" 
            extra={
              <Text type="secondary">
                Manage API tokens for third-party integrations
              </Text>
            }
          >
            <Alert
              message="About API Tokens"
              description="API tokens allow external applications to access the Vitreous Support API securely. Each token is tied to your account and can be revoked at any time. The token value is only shown once during creation - make sure to save it securely."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form
              form={apiTokenForm}
              layout="inline"
              onFinish={(values) => createApiToken.mutate(values)}
              style={{ marginBottom: 24 }}
              initialValues={{ permission: 'read_write' }}
            >
              <Form.Item
                name="name"
                rules={[{ required: true, message: 'Token name is required' }]}
                style={{ width: 200 }}
              >
                <Input placeholder="Token name (e.g., Mobile App)" />
              </Form.Item>
              <Form.Item name="description" style={{ width: 250 }}>
                <Input placeholder="Description (optional)" />
              </Form.Item>
              <Form.Item
                name="permission"
                rules={[{ required: true, message: 'Permission is required' }]}
                style={{ width: 150 }}
              >
                <Select placeholder="Permission">
                  <Option value="read">Read Only</Option>
                  <Option value="read_write">Read & Write</Option>
                </Select>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={createApiToken.isLoading}
                >
                  Generate Token
                </Button>
              </Form.Item>
            </Form>

            <Table
              dataSource={apiTokens}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Name',
                  dataIndex: 'name',
                  key: 'name',
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                  render: (text: string) => text || '-',
                },
                ...(user?.role === 'admin' ? [{
                  title: 'Created By',
                  key: 'created_by',
                  render: (_: any, record: any) => (
                    <span>
                      {record.user_name || record.user_email || 'Unknown'}
                    </span>
                  ),
                }] : []),
                {
                  title: 'Permission',
                  dataIndex: 'permission',
                  key: 'permission',
                  render: (permission: string) => (
                    <Tag color={permission === 'read_write' ? 'green' : 'blue'}>
                      {permission === 'read_write' ? 'Read & Write' : 'Read Only'}
                    </Tag>
                  ),
                },
                {
                  title: 'Created',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (date: string) => new Date(date).toLocaleString(),
                },
                {
                  title: 'Last Used',
                  dataIndex: 'last_used_at',
                  key: 'last_used_at',
                  render: (date: string) => date ? new Date(date).toLocaleString() : 'Never',
                },
                {
                  title: 'Status',
                  dataIndex: 'is_active',
                  key: 'is_active',
                  render: (active: boolean) => (
                    <span style={{ color: active ? '#52c41a' : '#ff4d4f' }}>
                      {active ? 'Active' : 'Revoked'}
                    </span>
                  ),
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_: any, record: any) => (
                    record.is_active ? (
                      <Popconfirm
                        title="Are you sure you want to revoke this token?"
                        description="This action cannot be undone and will immediately invalidate the token."
                        onConfirm={() => revokeApiToken.mutate(record.id)}
                        okText="Revoke"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                          Revoke
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Popconfirm
                        title="Are you sure you want to permanently delete this token?"
                        description="This action cannot be undone. The token will be permanently removed from the database."
                        onConfirm={() => deleteApiToken.mutate(record.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button type="link" danger icon={<DeleteOutlined />} size="small">
                          Delete
                        </Button>
                      </Popconfirm>
                    )
                  ),
                },
              ]}
            />
          </Card>
        </TabPane>

        {/* Role Management tab hidden for now */}
        {false && (
          <TabPane tab={<span><TeamOutlined />Role Management</span>} key="roles">
            <RoleManagement />
          </TabPane>
        )}

        <TabPane tab={<span><LaptopOutlined />Equipment</span>} key="equipment">
          <Equipment />
        </TabPane>

        {/* TV Dashboard Tab */}
        <TabPane tab={<span><DesktopOutlined />TV Dashboard</span>} key="tv-dashboard">
          <Card 
            title="TV Dashboard Password" 
            extra={
              <Text type="secondary">
                Set password for TV Dashboard access
              </Text>
            }
          >
            <Alert
              message="TV Dashboard Authentication"
              description="Set a password that will be used to access the TV Dashboard. This password will be required when accessing the TV Dashboard view."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form
              form={tvDashboardForm}
              layout="vertical"
              onFinish={(values) => setTvDashboardPassword.mutate(values)}
              style={{ maxWidth: 500 }}
            >
              <Form.Item
                name="password"
                label="TV Dashboard Password"
                rules={[
                  { required: true, message: 'Password is required' },
                  { min: 4, message: 'Password must be at least 4 characters' }
                ]}
              >
                <Input.Password 
                  placeholder="Enter TV Dashboard password"
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={setTvDashboardPassword.isLoading}
                >
                  Set Password
                </Button>
              </Form.Item>
            </Form>

            {tvDashboardPassword?.hasPassword && (
              <div style={{ marginTop: 24 }}>
                <Divider />
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Current Password:</Text>
                  <Space>
                    <Input
                      value={showTvPassword ? tvDashboardPassword.password : '••••••••'}
                      readOnly
                      style={{ width: 300, fontFamily: 'monospace' }}
                    />
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setShowTvPassword(!showTvPassword)}
                    >
                      {showTvPassword ? 'Hide' : 'Show'}
                    </Button>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => {
                        if (tvDashboardPassword.password) {
                          copyToClipboard(tvDashboardPassword.password);
                        }
                      }}
                    >
                      Copy
                    </Button>
                  </Space>
                </Space>
              </div>
            )}
          </Card>

          <Card 
            title="Call Me Alert" 
            extra={
              <Text type="secondary">
                Sound alert when Call Me queue has items
              </Text>
            }
            style={{ marginTop: 24 }}
          >
            <Alert
              message="TV Dashboard Call Me Alert"
              description="When enabled, the TV Dashboard will play an alert sound every few seconds while there are pending Call Me requests. Configure the interval below."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form
              layout="vertical"
              onFinish={(values) => setTvCallMeAlert.mutate({ enabled: values.enabled, interval: values.interval, tone: values.tone })}
              initialValues={tvCallMeAlert ? { enabled: tvCallMeAlert.enabled, interval: tvCallMeAlert.interval, tone: tvCallMeAlert.tone || 'beep' } : undefined}
              key={tvCallMeAlert ? `${tvCallMeAlert.enabled}-${tvCallMeAlert.interval}-${tvCallMeAlert.tone || 'beep'}` : 'empty'}
            >
              <Form.Item name="enabled" label="Enable Call Me Alert" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="tone" label="Alert Tone" help="Choose the sound played when Call Me queue has items">
                <Select style={{ width: 200 }}>
                  <Option value="beep">Beep (default)</Option>
                  <Option value="chime">Chime</Option>
                  <Option value="alert">Alert (urgent)</Option>
                  <Option value="soft">Soft</Option>
                  <Option value="double">Double beep</Option>
                </Select>
              </Form.Item>
              <Form.Item name="interval" label="Alert Interval (seconds)" help="How often to play the alert sound when Call Me queue has items (5-60 seconds)">
                <InputNumber min={5} max={60} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={setTvCallMeAlert.isLoading}>
                  Save
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card
            title="Main App Call Me Bell Sound"
            extra={
              <Text type="secondary">
                Sound for notification bell when Call Me tickets arrive
              </Text>
            }
            style={{ marginTop: 24 }}
          >
            <Alert
              message="Main App Notification Bell Sound"
              description="When enabled, users in the main app (not TV Dashboard) will hear a short sound when a new Call Me request arrives."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form
              layout="vertical"
              onFinish={(values) => setCallMeBellSound.mutate({ enabled: !!values.enabled })}
              initialValues={callMeBellSound ? { enabled: !!callMeBellSound.enabled } : undefined}
              key={callMeBellSound ? String(callMeBellSound.enabled) : 'empty'}
            >
              <Form.Item name="enabled" label="Enable Call Me Bell Sound" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={setCallMeBellSound.isLoading}>
                  Save
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card 
            title="TV Dashboard Announcement" 
            extra={
              <Text type="secondary">
                Announcement displayed on TV Dashboard
              </Text>
            }
            style={{ marginTop: 24 }}
          >
            <Alert
              message="TV Dashboard Announcement"
              description="Add an announcement that will scroll at the bottom of the TV Dashboard. You can use emojis in your announcement! Leave empty to remove the announcement."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form
              form={announcementForm}
              layout="vertical"
              onFinish={(values) => setTvDashboardAnnouncement.mutate(values)}
              initialValues={{ announcement: tvDashboardAnnouncement?.announcement || '' }}
              key={tvDashboardAnnouncement?.announcement || 'empty'}
            >
              <Form.Item
                name="announcement"
                label="Announcement Text"
                help="This text will scroll at the bottom of the TV Dashboard. Emojis are supported! 🎉"
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter announcement text (e.g., 🎉 Welcome to Vitreous Support! System maintenance scheduled for tonight at 10 PM.)"
                  maxLength={500}
                  showCount
                  allowClear
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={setTvDashboardAnnouncement.isLoading}
                  >
                    Save Announcement
                  </Button>
                  {tvDashboardAnnouncement?.hasAnnouncement && (
                    <Button
                      onClick={() => {
                        announcementForm.setFieldsValue({ announcement: '' });
                        setTvDashboardAnnouncement.mutate({ announcement: '' });
                      }}
                    >
                      Clear Announcement
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>

            {tvDashboardAnnouncement?.hasAnnouncement && (
              <div style={{ marginTop: 24 }}>
                <Divider />
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Current Announcement Preview:</Text>
                  <div style={{ 
                    padding: '16px', 
                    background: '#f5f5f5', 
                    borderRadius: '4px',
                    fontSize: '16px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {tvDashboardAnnouncement.announcement}
                  </div>
                </Space>
              </div>
            )}
          </Card>

          <Card
            title="TV Dashboard News Feed"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateTvNewsModal}>
                Add News Item
              </Button>
            }
            style={{ marginTop: 24 }}
          >
            <Alert
              message="News Feed"
              description="Add news items that rotate on the TV Dashboard main content area. Use this for team updates, reminders, or operational notices. Product version and release logs are shown automatically from the deployed version."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              rowKey="id"
              dataSource={tvDashboardNews}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Title',
                  dataIndex: 'title',
                  key: 'title',
                  ellipsis: true,
                },
                {
                  title: 'Preview',
                  dataIndex: 'content',
                  key: 'content',
                  ellipsis: true,
                  render: (text: string) => text?.slice(0, 80) + (text?.length > 80 ? '…' : ''),
                },
                {
                  title: 'Order',
                  dataIndex: 'sortOrder',
                  key: 'sortOrder',
                  width: 80,
                },
                {
                  title: 'Active',
                  dataIndex: 'isActive',
                  key: 'isActive',
                  width: 90,
                  render: (active: boolean) => (
                    <Tag color={active !== false ? 'green' : 'default'}>
                      {active !== false ? 'Yes' : 'No'}
                    </Tag>
                  ),
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  width: 160,
                  render: (_: unknown, record: TvDashboardNewsItem) => (
                    <Space>
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEditTvNewsModal(record)}>
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete this news item?"
                        onConfirm={() => deleteTvNews.mutate(record.id)}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        </TabPane>
      </Tabs>

      <Modal
        title={editingTvNews ? 'Edit News Item' : 'Add News Item'}
        open={tvNewsModalVisible}
        onCancel={closeTvNewsModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={tvNewsForm}
          layout="vertical"
          onFinish={handleSaveTvNews}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Title is required' }]}
          >
            <Input placeholder="News headline" maxLength={500} showCount />
          </Form.Item>
          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true, message: 'Content is required' }]}
          >
            <Input.TextArea rows={5} placeholder="News details shown on the TV Dashboard" />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort Order" help="Lower numbers appear first">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={createTvNews.isLoading || updateTvNews.isLoading}
              >
                {editingTvNews ? 'Update' : 'Create'}
              </Button>
              <Button onClick={closeTvNewsModal}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* API Token Value Modal */}
      <Modal
        title="API Token Created Successfully"
        open={tokenModalVisible}
        onCancel={() => {
          setTokenModalVisible(false);
          setNewTokenValue(null);
        }}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => newTokenValue && copyToClipboard(newTokenValue)}
          >
            Copy Token
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setTokenModalVisible(false);
              setNewTokenValue(null);
            }}
          >
            Close
          </Button>,
        ]}
      >
        <Alert
          message="Save this token now!"
          description="For security reasons, this token will only be shown once. Make sure to copy it and store it securely."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: 4, wordBreak: 'break-all', fontFamily: 'monospace' }}>
          {newTokenValue}
        </div>
        <Divider />
        <Text type="secondary">
          Use this token in the Authorization header as: <code>Bearer {newTokenValue?.substring(0, 20)}...</code>
        </Text>
      </Modal>

    </div>
  );
};

export default Settings;
